import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2, AlertTriangle, LogOut, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { QrScanner } from "../components/traffic/QrScanner";
import { DelayNotice } from "../components/traffic/DelayNotice";
import { ChangePassword } from "../components/site/ChangePassword";
import { FuelLoad } from "../components/fleet/FuelLoad";
import { useAuth } from "../lib/auth";
import { api, apiError } from "../lib/api";

const short = (s) => (s ?? "").split(" · ")[0];

const TripList = ({ onOpen }) => {
  const [trips, setTrips] = useState(null);
  useEffect(() => { api.get("/driver/trips").then((r) => setTrips(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(apiError(e)); setTrips([]); }); }, []);
  if (!trips) return <Loader2 className="h-6 w-6 animate-spin" />;
  if (!trips.length) return <p data-testid="driver-no-trips" className="rounded-2xl bg-white p-6 text-[var(--muted-ink)]">No tenés viajes asignados en los próximos días.</p>;
  return (
    <div className="space-y-3">
      {trips.map((t) => (
        <button key={t.id} onClick={() => onOpen(t.id)} data-testid={`driver-trip-${t.id}`} className="w-full rounded-3xl bg-white p-5 text-left ring-1 ring-black/5 transition-transform active:scale-[0.99]">
          <p className="font-mono text-xs text-[var(--forest)]">{t.date} · {t.departure} hs</p>
          <p className="mt-1 font-display-up text-lg">{t.route_name}</p>
          <p className="mt-1 text-sm text-[var(--muted-ink)]">Subieron {t.boarded} de {t.passengers} pasajeros</p>
        </button>
      ))}
    </div>
  );
};

const who = (p) => (p.pax && Object.keys(p.pax).length ? p.seats.map((s) => `${s} ${p.pax[s]?.name ?? p.name} (DNI ${p.pax[s]?.dni ?? p.dni})`).join(" · ") : `${p.seats.join(", ")} ${p.name} · DNI ${p.dni}`);

const Result = ({ r }) => r && (
  <div data-testid="board-result" className={`rounded-3xl p-5 ${r.error ? "bg-red-50 text-red-900" : r.already ? "bg-amber-50 text-amber-900" : "bg-[var(--lime)] text-[var(--ink)]"}`}>
    <p className="flex items-center gap-2 font-bold">{r.error ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}{r.error ? "No puede subir" : r.already ? "Ya estaba marcado" : "Puede subir"}</p>
    {r.error ? <p className="mt-1 text-sm">{r.error}</p> : (
      <p className="mt-1 text-lg"><b>Butaca</b> {who(r)}<span className="block text-sm">{short(r.from_stop)} → {short(r.to_stop)}{r.status === "reservado" && " · RESERVA SIN COBRAR"}</span></p>
    )}
  </div>
);

const Stops = ({ d, id, onDone }) => {
  const [last, setLast] = useState(null);
  const pax = d.passengers ?? [];
  const dep = d.trip.departures ?? {};
  const go = async (i) => {
    const missing = pax.filter((p) => p.from_idx === i && !p.boarded_at).length;
    if (!window.confirm(`¿Salimos de ${short(d.stops[i].name)}?${missing ? ` ${missing} pasaje(s) no subieron: se avisa a la boletería.` : ""}`)) return;
    try { const { data } = await api.post(`/driver/trips/${id}/depart`, { stop_idx: i }); setLast(data); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  const rows = d.stops.slice(0, -1).map((s, i) => ({ s, i, up: pax.filter((p) => p.from_idx === i) })).filter((x) => x.up.length || dep[x.i]);
  return (
    <div data-testid="driver-stops" className="rounded-3xl bg-white p-4 ring-1 ring-black/5">
      <p className="mb-2 font-display-up text-lg">Paradas con subida</p>
      {rows.length === 0 && <p className="text-sm text-[var(--muted-ink)]">No hay pasajeros que suban en este viaje.</p>}
      <div className="divide-y divide-black/5">
        {rows.map(({ s, i, up }) => (
          <div key={i} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0"><p className="font-semibold">{short(s.name)} <span className="font-mono text-xs">{s.time}</span></p><p className="text-xs text-[var(--muted-ink)]">Subieron {up.filter((p) => p.boarded_at).length} de {up.length}</p></div>
            {dep[i]
              ? <span data-testid={`stop-departed-${i}`} className="shrink-0 rounded-full bg-[var(--paper)] px-3 py-2 text-xs font-bold">Salió {new Date(dep[i].at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}{dep[i].no_shows ? ` · ${dep[i].no_shows} no subieron` : ""}</span>
              : <button onClick={() => go(i)} data-testid={`stop-depart-${i}`} className="shrink-0 rounded-full bg-[var(--forest)] px-4 py-2 text-xs font-bold text-white">Salimos</button>}
          </div>
        ))}
      </div>
      {last && (
        <div data-testid="depart-result" className={`mt-3 rounded-2xl p-3 text-sm ${last.no_shows.length ? "bg-red-50 text-red-900" : "bg-[var(--lime)]"}`}>
          {last.no_shows.length ? <>Avisamos que no subieron en {short(last.stop)}: {last.no_shows.map((p) => `${p.seats.join(",")} ${p.name}`).join(" · ")}</> : `Salida de ${short(last.stop)} registrada: subieron todos.`}
        </div>
      )}
    </div>
  );
};

const Boarding = ({ id, onBack }) => {
  const [d, setD] = useState(null);
  const [res, setRes] = useState(null);
  const [code, setCode] = useState("");
  const load = () => api.get(`/driver/trips/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const board = async (c) => {
    try { const { data } = await api.post(`/driver/trips/${id}/board`, { code: c }); setRes(data); navigator.vibrate?.(80); load(); } catch (e) { setRes({ error: apiError(e) }); navigator.vibrate?.([60, 60, 60]); }
  };
  const undo = async (c) => { await api.post(`/driver/trips/${id}/unboard`, { code: c }).catch((e) => toast.error(apiError(e))); load(); };
  if (!d) return <Loader2 className="h-6 w-6 animate-spin" />;
  const pax = d.passengers ?? [];
  const seats = (l) => l.reduce((n, p) => n + p.seats.length, 0);
  const up = (l) => l.reduce((n, p) => n + (p.boarded_at ? p.seats.length : (p.boarded_seats?.length ?? 0)), 0);
  return (
    <div className="space-y-4">
      <button onClick={onBack} data-testid="driver-back" className="inline-flex items-center gap-2 text-sm font-bold"><ArrowLeft className="h-4 w-4" /> Mis viajes</button>
      <div><p className="font-mono text-xs text-[var(--forest)]">{d.trip.date} · {d.trip.departure} hs</p><h2 className="font-display text-3xl">{d.route_name}</h2></div>
      <p data-testid="driver-count" className="rounded-2xl bg-white p-4 text-center font-display-up text-2xl ring-1 ring-black/5">Subieron {up(pax)} / {seats(pax)}</p>
      <QrScanner onCode={board} />
      <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) { board(code.trim()); setCode(""); } }} className="flex gap-2">
        <Input data-testid="driver-code-input" placeholder="Código a mano (SG-XXXXXX)" value={code} onChange={(e) => setCode(e.target.value)} className="h-12 rounded-xl font-mono uppercase" />
        <button data-testid="driver-code-submit" className="h-12 rounded-xl bg-[var(--forest)] px-5 font-bold text-white">Marcar</button>
      </form>
      <Result r={res} />
      <Stops d={d} id={id} onDone={load} />
      <DelayNotice endpoint={`/driver/trips/${id}/delay`} delays={d.trip.delays ?? []} onSent={load} />
      <div className="divide-y divide-black/5 rounded-3xl bg-white ring-1 ring-black/5" data-testid="driver-passengers">
        {pax.length === 0 && <p className="p-5 text-sm text-[var(--muted-ink)]">Sin pasajeros vendidos.</p>}
        {pax.map((p) => (
          <div key={p.code} className="flex items-center justify-between gap-3 p-4">
            <div className="min-w-0"><p className="font-semibold" data-testid={`driver-pax-${p.code}`}>{who(p)}</p><p className="text-xs text-[var(--muted-ink)]">Sube {short(d.stops[p.from_idx]?.name)} {d.stops[p.from_idx]?.time} · baja {short(d.stops[p.to_idx]?.name)}</p></div>
            {p.boarded_at
              ? <button onClick={() => undo(p.code)} data-testid={`unboard-${p.code}`} className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--lime)] px-3 py-2 text-xs font-bold"><CheckCircle2 className="h-4 w-4" /> Subió <Undo2 className="h-3 w-3" /></button>
              : <button onClick={() => board(p.code)} data-testid={`board-${p.code}`} className="shrink-0 rounded-full border border-black/15 px-3 py-2 text-xs font-bold">{p.boarded_seats?.length ? `Subió ${p.boarded_seats.length}/${p.seats.length} · marcar resto` : "Marcar"}</button>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Conductor() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(null);
  if (user === null) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (user === false) return <Navigate to="/admin" replace />;
  if (!["conductor", "admin"].includes(user.role)) return <Navigate to="/admin/trafico" replace />;
  return (
    <section data-testid="driver-page" className="min-h-[75vh] bg-[var(--paper)] pb-24">
      <div className="mx-auto max-w-xl px-5 pt-10">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div><p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--forest)]">Conductor · {user.name}</p><h1 className="mt-2 font-display text-4xl">Embarque</h1></div>
          <div className="flex flex-col items-end gap-2"><ChangePassword /><button onClick={logout} data-testid="driver-logout" className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-semibold"><LogOut className="h-4 w-4" /> Salir</button></div>
        </div>
        {open ? <Boarding id={open} onBack={() => setOpen(null)} /> : <><FuelLoad /><TripList onOpen={setOpen} /></>}
      </div>
    </section>
  );
}
